/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { getTelemetryObject, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import * as Msdyn365 from '@msdyn365-commerce/core';
import classnames from 'classnames';
import * as React from 'react';

import { ModalProvider } from '../../components/modal';
import { contentBlockAdditionalContent } from './components/additional-content';
import LinksComponent from './components/links';
import ProductRequestButton from './components/product-request-button';
import { ILxs2ColumnContentBlockFullConfig, ILxs2ColumnContentBlockViewProps } from './lxs-2-column-content-block.data';
import {
    ICtaButtonData,
    ILinksData,
    ILxs2ColumnContentBlockProps,
    imagePlacement as imagePlacementOption,
    moduleLayout as moduleLayoutOption,
    actionableRegion as region,
    width as widthOption
} from './lxs-2-column-content-block.props.autogenerated';

const CLASS_NAME = 'lxs-2-column-content-block';
export class Lxs2ColumnContentBlock extends React.PureComponent<ILxs2ColumnContentBlockProps<{}>> {
    private readonly telemetryContent: ITelemetryContent = getTelemetryObject(
        this.props.context.request.telemetryPageName!,
        this.props.friendlyName,
        this.props.telemetry
    );

    public render(): JSX.Element | null {
        const config = this.props.config as ILxs2ColumnContentBlockFullConfig;
        const {
            heading,
            paragraph,
            image,
            links,
            className,
            imageAriaLabel,
            width = widthOption.container,
            imagePlacement = imagePlacementOption.left,
            moduleLayout = moduleLayoutOption.default,
            preheading,
            ctaButton,
            anchorId,
            textTheme,
            showProductRequestButton
        } = config;

        const contentBlockPreheading = preheading && (
            <Msdyn365.Text
                className={`${CLASS_NAME}__preheading`}
                tag='div'
                text={preheading}
                editProps={{ onEdit: this.handlePreheadingChange, requestContext: this.props.context.request }}
            />
        );

        const contentBlockTitle = heading && (
            <Msdyn365.Text
                className={`${CLASS_NAME}__title`}
                tag={heading.tag ?? 'h2'}
                text={heading.text}
                editProps={{ onEdit: this.handleTextChange, requestContext: this.props.context.request }}
            />
        );

        const contentBlockLinks = links && ArrayExtensions.hasElements(links) && (
            <LinksComponent
                {...{
                    CLASS_NAME: CLASS_NAME,
                    links,
                    onTextChange: this.handleLinkTextChange,
                    requestContext: this.props.context.request,
                    telemetryContent: this.telemetryContent,
                    role: 'link',
                    variant: 'links'
                }}
            />
        );

        const contentBlockCtaButton =
            ctaButton?.linkText &&
            (showProductRequestButton ? (
                <ProductRequestButton
                    buttonText={ctaButton.linkText}
                    actionContext={this.props.context.actionContext}
                    user={this.props.context.request.user}
                    resources={this.props.resources}
                    CLASS_NAME={CLASS_NAME}
                />
            ) : (
                <LinksComponent
                    {...{
                        CLASS_NAME: CLASS_NAME,
                        links: [this._mapCtaButtonToLinkData(ctaButton)],
                        onTextChange: this.handleCtaButtonTextChange,
                        requestContext: this.props.context.request,
                        telemetryContent: this.telemetryContent,
                        role: 'button',
                        variant: 'button'
                    }}
                />
            ));

        const contentBlockText = paragraph && (
            <Msdyn365.RichTextComponent
                text={paragraph}
                className={`${CLASS_NAME}__text`}
                editProps={{ onEdit: this.handleParagraphChange, requestContext: this.props.context.request }}
            />
        );

        const imageProps = {
            gridSettings: this.props.context.request.gridSettings ?? {},
            imageSettings: image?.imageSettings
        };

        const contentBlockImage = image && (
            <Msdyn365.Image
                {...image}
                {...imageProps}
                preserveImageSpace={true}
                editProps={{
                    key: config.image ?? {},
                    requestContext: this.props.context.request,
                    moduleType: this.props.typeName,
                    imagePropertyName: 'image',
                    moduleId: this.props.id,
                    layout: config.msdyn365__moduleLayout
                }}
                shouldSkipToMainImage
            />
        );

        const contentBlockDetailsStyle: React.CSSProperties | undefined = config.backgroundColor
            ? { backgroundColor: config.backgroundColor }
            : undefined;

        if (!contentBlockPreheading && !contentBlockTitle && !contentBlockText && !contentBlockLinks && !contentBlockCtaButton) {
            this.props.context.telemetry.error('Content block content is empty, module wont render.');
            return null;
        }

        const contentBlockviewProps: ILxs2ColumnContentBlockViewProps = {
            ...this.props,
            preheading: contentBlockPreheading,
            title: contentBlockTitle,
            text: contentBlockText,
            image: contentBlockImage,
            links: contentBlockLinks,
            ctaButton: contentBlockCtaButton,
            contentBlockContainer: {
                moduleProps: this.props,
                className: classnames(CLASS_NAME, className, textTheme && `texttheme__${textTheme}`, {
                    [`${CLASS_NAME}--full-width`]: width === widthOption.fullWidth,
                    [`${CLASS_NAME}--container`]: width !== widthOption.fullWidth,
                    [`${CLASS_NAME}--image-left`]: imagePlacement !== imagePlacementOption.right,
                    [`${CLASS_NAME}--image-right`]: imagePlacement === imagePlacementOption.right
                }),
                'data-m-layout': moduleLayout,
                id: this._sanitizeAnchorId(anchorId)
            },
            innerContainer: {
                className: `${CLASS_NAME}__inner`
            },
            imageContainer: { className: `${CLASS_NAME}__image` },
            detailsContainer: { className: `${CLASS_NAME}__details`, style: contentBlockDetailsStyle },
            contentBlockAnchorTag: {
                tag: 'a',
                className: `${CLASS_NAME}__image-link`,
                role: 'link'
            },
            imageLink: this._getImageLink(),
            imageAriaLabel,
            additionalContent: contentBlockAdditionalContent({
                requestContext: this.props.context.request,
                additionalContent: config.additionalContent,
                handleAdditionalTextChange: this.handleAdditionalTextChange,
                handleAdditionalParagraphChange: this.handleAdditionalParagraphChange,
                handleAdditionalLinkTextChange: this.handleAdditionalLinkTextChange,
                CLASS_NAME: CLASS_NAME
            }),
            handleAdditionalText: this.handleAdditionalTextChange,
            handleAdditionalParagraph: this.handleAdditionalParagraphChange,
            handleAdditionalLinkTextChange: this.handleAdditionalLinkTextChange
        };

        return <ModalProvider props={this.props}>{this.props.renderView(contentBlockviewProps) as React.ReactElement}</ModalProvider>;
    }

    public handlePreheadingChange = (event: Msdyn365.ContentEditableEvent) => {
        this.props.config.preheading = event.target.value;
    };

    public handleTextChange = (event: Msdyn365.ContentEditableEvent) => {
        this.props.config.heading!.text = event.target.value;
    };

    public handleParagraphChange = (event: Msdyn365.ContentEditableEvent) => {
        this.props.config.paragraph = event.target.value;
    };

    public handleLinkTextChange = (linkIndex: number) => (event: Msdyn365.ContentEditableEvent) => {
        const linkItem = this.props.config.links?.[Number(linkIndex)];

        if (linkItem?.link) {
            linkItem.link.linkText = event.target.value;
        }
    };

    public handleCtaButtonTextChange = () => (event: Msdyn365.ContentEditableEvent) => {
        if (this.props.config.ctaButton) {
            this.props.config.ctaButton.linkText = event.target.value;
        }
    };

    public handleAdditionalTextChange(index: number, event: Msdyn365.ContentEditableEvent): void {
        if (this.props.config.additionalContent) {
            this.props.config.additionalContent[Number(index)].heading = event.target.value;
        }
    }

    public handleAdditionalLinkTextChange(linkIndex: number, index: number, event: Msdyn365.ContentEditableEvent): void {
        const additionalContentObject =
            (this.props.config.additionalContent &&
                ArrayExtensions.hasElements(this.props.config.additionalContent) &&
                this.props.config.additionalContent[Number(index)]) ||
            {};

        if (additionalContentObject.links) {
            additionalContentObject.links[Number(linkIndex)].linkText = event.target.value;
        }
    }

    public handleAdditionalParagraphChange(index: number, event: Msdyn365.ContentEditableEvent): void {
        if (this.props.config.additionalContent) {
            this.props.config.additionalContent[Number(index)].subtext = event.target.value;
        }
    }

    private _getImageLink(): string | null {
        const { imageLink, links, actionableRegion } = this.props.config;

        if (actionableRegion === region.imageAndLinks) {
            if (imageLink?.destinationUrl) {
                return imageLink.destinationUrl;
            }

            const firstLink = links?.[0]?.link;

            if (firstLink?.linkUrl?.destinationUrl) {
                return firstLink.linkUrl.destinationUrl;
            }

            return null;
        }

        return null;
    }

    private _mapCtaButtonToLinkData(ctaButton: ICtaButtonData): ILinksData {
        return {
            link: {
                linkText: ctaButton.linkText,
                linkUrl: ctaButton.linkUrl,
                ariaLabel: ctaButton.ariaLabel,
                openInNewTab: ctaButton.openInNewTab
            }
        } as ILinksData;
    }

    private _sanitizeAnchorId(anchorId?: string): string | undefined {
        if (!anchorId) {
            return undefined;
        }

        const sanitizedAnchorId = anchorId
            .trim()
            .replace(/\s+/g, '-')
            .replace(/[^A-Za-z0-9-_:.]/g, '');

        return sanitizedAnchorId || undefined;
    }
}

export default Lxs2ColumnContentBlock;
